/**
 * lib/experiences/descriptionDisplay.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Frontend-only display cleanup for the short description text shown on a
 * marketplace card (supplier_explanation / editorial_note / description).
 * Never mutates the underlying ExperienceItem, never touches booking_url or
 * any field used for booking — this only changes what's rendered on a card.
 *
 * Marketplace Filter Polish V2.1, task 2: production audit found real
 * backend-generated supplier explanations using itinerary-flavored wording
 * ("Buena opción para una ruta histórica: combina museo; 4.0/5 con 13
 * reseñas.") — this is real backend copy (Real Data Policy content, not
 * frontend-invented), so it is fixed here as a display-only substitution
 * rather than by touching backend generation. Mechanical word-level swap
 * only ("ruta"/"itinerario" → "experiencia") — never rewrites the sentence,
 * never touches the rating/review numbers or any other content.
 */

// Whole-word, accent/case-insensitive. "ruta" and "itinerario" read as
// day-by-day trip-planning language, which the Experiences marketplace
// deliberately avoids — "experiencia" is the neutral, always-safe swap.
const ITINERARY_WORD_REPLACEMENTS: ReadonlyArray<readonly [string, string]> = [
  ["itinerario", "experiencia"],
  ["ruta", "experiencia"],
];

/**
 * Preserves the original word's capitalization pattern on the replacement,
 * so a sentence-initial "Ruta" becomes "Experiencia", not "experiencia".
 *
 * Builds a brand-new RegExp on every call rather than reusing a shared
 * global-flagged instance — a shared `g`-flag RegExp carries `lastIndex`
 * state across calls, and this codebase's build pipeline was observed to
 * leak that state across otherwise-independent invocations (confirmed via
 * regression test), so a fresh object per call is the only reliable fix.
 */
function replaceWordPreservingCase(text: string, word: string, replacement: string): string {
  const pattern = new RegExp(`\\b${word}\\b`, "giu");
  return text.replace(pattern, (match) => {
    if (match === match.toUpperCase() && match.length > 1) return replacement.toUpperCase();
    if (match[0] === match[0].toUpperCase()) {
      return replacement.charAt(0).toUpperCase() + replacement.slice(1);
    }
    return replacement;
  });
}

export function normalizeExperienceDescriptionForDisplay(text: string | null | undefined): string | null {
  if (!text) return text ?? null;
  let result = text;
  for (const [word, replacement] of ITINERARY_WORD_REPLACEMENTS) {
    result = replaceWordPreservingCase(result, word, replacement);
  }
  return result;
}

// See descriptionDisplay.test.ts for regression coverage.
